import { useEffect, useRef } from "react"
import {
  ThreadPrimitive,
  MessagePrimitive,
  useThreadRuntime,
} from "@assistant-ui/react"
import { useAuiState } from "@assistant-ui/store"
import { HugeiconsIcon } from "@hugeicons/react"
import { AiChat02Icon } from "@hugeicons/core-free-icons"
import { useChatPanel } from "../hooks/use-chat-panel"
import { getActiveDraft } from "../api/assistant-api"
import { toThreadMessages } from "../utils/to-thread-messages"
import { ChatComposer } from "./chat-composer"
import { WelcomeCards } from "./welcome-cards"

function LoadingDots() {
  return (
    <span className="mt-1 inline-flex items-center gap-1">
      <span className="chat-loading-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
      <span className="chat-loading-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
      <span className="chat-loading-dot h-1.5 w-1.5 rounded-full bg-muted-foreground" />
    </span>
  )
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className="flex justify-end animate-in fade-in slide-in-from-bottom-1 duration-200">
      <div className="max-w-[80%] rounded-2xl bg-primary px-3.5 py-2 text-sm text-primary-foreground shadow-sm">
        <MessagePrimitive.Content />
      </div>
    </MessagePrimitive.Root>
  )
}

function AssistantMessage() {
  const isRunning = useAuiState((s) => s.message?.status?.type === "running")

  return (
    <MessagePrimitive.Root className="flex items-start gap-2 animate-in fade-in slide-in-from-bottom-1 duration-200">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <HugeiconsIcon icon={AiChat02Icon} size={12} />
      </div>
      <div className="max-w-[calc(100%-2rem)] rounded-2xl bg-muted px-3.5 py-2 text-sm shadow-sm">
        <MessagePrimitive.Content />
        {isRunning && <LoadingDots />}
      </div>
    </MessagePrimitive.Root>
  )
}

function WelcomeScreen() {
  return (
    <ThreadPrimitive.Empty>
      <WelcomeCards />
    </ThreadPrimitive.Empty>
  )
}

/** Loads the user's active thread (if any) and populates the runtime.
 *
 * Skips when the runtime *already has messages* — that means either the
 * history panel just pushed a past thread (don't clobber it), or this
 * mount is a re-open mid-session and the runtime survived. Otherwise
 * this fires on first mount and on every panel re-open after a close
 * (the Sheet unmounts content on close, so the runtime is empty even
 * though ``threadId`` is still preserved in the panel store).
 *
 * Sets ``threadHydrated`` to ``true`` once the fetch settles (or is
 * skipped) so other mount-time effects — notably the composer's
 * page-context auto-chip — can wait for the conversation to be loaded
 * before deciding what to attach. Without this, the composer fires
 * before the messages arrive and re-attaches the order on every reopen
 * even though the conversation already references it.
 */
function ThreadLoader() {
  const threadRuntime = useThreadRuntime()
  const setThreadId = useChatPanel((s) => s.setThreadId)
  const setThreadStatus = useChatPanel((s) => s.setThreadStatus)
  const setThreadHydrated = useChatPanel((s) => s.setThreadHydrated)
  const markAssistantReplied = useChatPanel((s) => s.markAssistantReplied)
  const loaded = useRef(false)

  useEffect(() => {
    if (loaded.current) return
    loaded.current = true

    // The runtime starts with one empty assistant placeholder; treat
    // anything > 1 message OR a non-empty first message as "already
    // populated" and skip the fetch.
    const existing = threadRuntime.getState().messages ?? []
    const alreadyHydrated =
      existing.length > 1 ||
      (existing[0]?.content?.length ?? 0) > 0
    if (alreadyHydrated) {
      setThreadHydrated(true)
      return
    }

    getActiveDraft()
      .then((issue) => {
        if (!issue || issue.conversation_transcript.length === 0) return
        setThreadId(issue.id)
        setThreadStatus(issue.status)
        const messages = toThreadMessages(issue.conversation_transcript)
        threadRuntime.reset(messages)
        if (messages.some((m) => m.role === "assistant")) {
          markAssistantReplied()
        }
      })
      .catch(() => {
        // If fetch fails, just show the welcome screen
      })
      .finally(() => {
        setThreadHydrated(true)
      })
  }, [
    threadRuntime,
    setThreadId,
    setThreadStatus,
    setThreadHydrated,
    markAssistantReplied,
  ])

  return null
}

export function IssueReportThread() {
  return (
    <ThreadPrimitive.Root className="flex h-full flex-col">
      <ThreadLoader />
      <ThreadPrimitive.Viewport className="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
        <WelcomeScreen />
        <ThreadPrimitive.Messages
          components={{ UserMessage, AssistantMessage }}
        />
      </ThreadPrimitive.Viewport>

      <ChatComposer />
    </ThreadPrimitive.Root>
  )
}
